import React from 'react';
import { Helmet } from 'react-helmet';
import MandationCalculator from '../../components/tools/MandationCalculator';
import DigitalLinkChecker from '../../components/tools/DigitalLinkChecker';
import PenaltyCalculator from '../../components/tools/PenaltyCalculator';
import WFHUpdate from '../../components/tools/WFHUpdate';
import OriginalStat from '../../components/tools/OriginalStat';
import Roadmap from '../../components/tools/Roadmap';
import MTDFactHub from '../../components/tools/MTDFactHub';
import TableOfContents from '../../components/ui/TableOfContents';
import BackToTop from '../../components/ui/BackToTop';
import SectionSummary from '../../components/ui/SectionSummary';
import MixedIncomeSeparationGuide from '../../components/tools/MixedIncomeSeparationGuide';
import WhyChooseZap from '../../components/tools/WhyChooseZap';
import {
  MTD_PHASES,
  PENALTY_EASEMENT,
  POSITIONING,
  QUARTERLY_DEADLINES_2026_27,
  QUARTERLY_DEADLINES_SOURCE,
  getNextQuarterlyDeadline,
} from '../../compliance/mtdFacts';

const MtdGuidePage = () => {
  const tocSections = [
    { title: "Who is mandated for MTD in April 2026?", id: "mandation" },
    { title: "Who is Zap made for?", id: "who-is-zap-for" },
    { title: "Are Mixed Bank Feeds a Compliance Risk?", id: "mixed-income-separation" },
    { title: "Are Excel spreadsheets still MTD compliant?", id: "digital-link" },
    { title: "The Role of Bridging Software in 2026", id: "bridging-software" },
    { title: "How much are the HMRC late submission fines?", id: "penalty" },
    { title: "When do the new MTD rules roll out?", id: "roadmap" },
    { title: "Preparing for the April 2027 Threshold Drop", id: "2027-drop" },
    { title: "Can I still claim HMRC flat rates?", id: "wfh-update" },
    { title: "MTD for High-Income Employees with Side Hustles", id: "side-hustle" },
    { title: "Directory of Specific MTD Anxieties", id: "spoke-directory" },
    { title: "MTD Fact vs Fiction", id: "fact-hub" }
  ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "MTD 2026 Mandation Calculator",
    "operatingSystem": "Web",
    "applicationCategory": "FinanceApplication",
    "description": "Calculates if a UK sole trader or landlord is mandated for Making Tax Digital (MTD) based on gross turnover thresholds.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP"
    }
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to prepare records for MTD for Income Tax",
    "description": "Check mandation, keep digital records in compatible software, and capture receipts for your accountant.",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Check Your Mandation Date",
        "text": `Determine if your gross income exceeds the £${MTD_PHASES[0].thresholdGBP.toLocaleString('en-GB')} threshold to find out your MTD start date.`
      },
      {
        "@type": "HowToStep",
        "name": "Keep digital records in compatible software",
        "text": "Create digital records in software that works with MTD for Income Tax. Typing original entries is allowed. If you use more than one product, digitally link them (for example CSV import)."
      },
      {
        "@type": "HowToStep",
        "name": "Capture source documents",
        "text": `${POSITIONING.subHero} ${POSITIONING.noSubmit}`
      }
    ]
  };
  const nextQuarter = getNextQuarterlyDeadline();
  const firstQuarter = QUARTERLY_DEADLINES_2026_27[0];
  const deadlineLine = nextQuarter
    ? `Next 2026/27 quarterly update deadline: ${nextQuarter.label}.`
    : `The first 2026/27 quarterly update deadline was ${firstQuarter.label}.`;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Helmet>
        <title>MTD 2026 Mandation Checker | HMRC Compliance | Am I in scope for April 6?</title>
        <meta name="description" content="The Comprehensive 1,500+ Word Authority Guide to MTD 2026 Compliance. Check your mandation status, understand digital links, and calculate penalty risk." />
        <meta name="keywords" content="MTD 2026, HMRC compliant, Making Tax Digital, digital link, sole trader tax" />
        <script type="application/ld+json">{JSON.stringify([schema, howToSchema])}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-indigo-500/10 to-transparent pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 mb-16">
          <div className="inline-block px-4 py-1.5 text-xs font-bold tracking-widest text-indigo-400 uppercase bg-indigo-500/10 rounded-full border border-indigo-500/20 mb-6">
            MTD 2026 Compliance Hub
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6">
            Making Tax Digital<br />
            <span className="text-indigo-500">for ITSA</span>
          </h1>
          <p className="text-lg font-semibold text-slate-200 max-w-2xl mx-auto mb-2 leading-relaxed">
            Yes, you are mandated if your gross turnover exceeds £{MTD_PHASES[0].thresholdGBP.toLocaleString('en-GB')}.
            <span className="inline-block bg-indigo-500/20 text-indigo-200 px-3 py-1.5 rounded-lg ml-2 border border-indigo-500/30 text-sm mt-2 md:mt-0 shadow-inner">
              <span className="font-bold">Deadline:</span> {deadlineLine}
              <span className="block text-[10px] text-indigo-400/80 mt-1 italic border-t border-indigo-500/20 pt-1">
                Source: <a href={QUARTERLY_DEADLINES_SOURCE.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-300">{QUARTERLY_DEADLINES_SOURCE.sourceAlias}</a>
              </span>
            </span>
          </p>
          <p className="text-md text-slate-400 max-w-2xl mx-auto leading-relaxed">
            The Largest Change to UK Taxation in Decades. Use our tools below to check your status, diagnose compliance gaps, and understand the penalty system.
          </p>

        </div>

        <TableOfContents sections={tocSections} />

        <div className="relative z-10 space-y-12 max-w-4xl mx-auto">
          <div id="mandation">
            <MandationCalculator isEmbedded={true} />
          </div>

          <div id="who-is-zap-for" className="relative z-10">
            <WhyChooseZap isEmbedded={true} />
          </div>

          <div id="mixed-income-separation">
            <MixedIncomeSeparationGuide />
          </div>

          <div id="digital-link">
            <DigitalLinkChecker />
          </div>

          <div id="bridging-software" className="bg-slate-900/60 p-8 rounded-3xl border border-white/5">
            <h2 className="text-2xl font-bold text-white mb-4">Receipt Capture: The First Step</h2>
            <div className="prose prose-invert max-w-none text-slate-300">
              <p>
                Under MTD for Income Tax, records must be kept digitally in compatible software. While filing software handles the quarterly submission to HMRC, Zap Expenses provides the <strong>receipt capture and data extraction</strong> step — turning physical receipts into structured digital records.
              </p>
              <div className="h-8" />
              <p>
                By capturing receipts via <strong>WhatsApp</strong> and using AI to extract the details, Zap prepares structured data that is ready for review and export. This data can then be imported into your accounting or filing software. {POSITIONING.noSubmit}
              </p>
            </div>
            <SectionSummary
              takeaway={`${POSITIONING.subHero} ${POSITIONING.noSubmit}`}
              hmrcRef={<a href="https://www.gov.uk/government/publications/digital-record-keeping-notice-for-making-tax-digital-for-income-tax" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-300 underline">Digital record-keeping direction for Making Tax Digital</a>}
              actionText="Try the Receipt Speed Test →"
              actionLink="/speed-test"
            />
          </div>

          <div id="penalty">
            <PenaltyCalculator />
            <SectionSummary
              takeaway={PENALTY_EASEMENT.description}
              hmrcRef={<a href="https://www.gov.uk/guidance/penalties-for-making-tax-digital-for-income-tax" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-300 underline">HMRC Penalty Points System</a>}
              actionText="Calculate your Penalty Risk"
              actionLink="#penalty"
            />
          </div>

          <div id="roadmap">
            <Roadmap />
          </div>

          {/* New Section 2 */}
          <div id="2027-drop" className="bg-slate-900/60 p-8 rounded-3xl border border-white/5">
            <h2 className="text-2xl font-bold text-white mb-4">Preparing for the April 2027 Threshold Drop</h2>
            <div className="prose prose-invert max-w-none text-slate-300">
              <p>
                While the April 2026 deadline only captures those with qualifying income over £50,000, <strong>April 2027 lowers this threshold to £30,000</strong>.
                According to the HMRC Tax Information and Impact Note (TIIN), this expansion will bring an additional <strong>1,077,000 sole traders and landlords</strong> into the MTD system.
              </p>
              <div className="h-8" />
              <p>
                HMRC estimates the <strong>average transitional cost per business will be £350</strong>. Setting up your digital capture software now, before the 2027 rush, ensures you have a historical baseline and allows you to test your WhatsApp automations under the 'soft landing' period.
              </p>
            </div>
            <SectionSummary
              takeaway="The MTD mandation threshold drops to £30k in April 2027, bringing 1.07M more businesses into scope with estimated transitional costs of £350 per business."
              hmrcRef={<><a href="https://www.legislation.gov.uk/uksi/2026/336/contents/made" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-300 underline">MTD Regulations 2026</a></>}
              actionText="Review the 2026/27 Roadmap"
              actionLink="#roadmap"
            />
          </div>

          <div id="wfh-update">
            <WFHUpdate />
          </div>

          {/* New Section 3 */}
          <div id="side-hustle" className="bg-slate-900/60 p-8 rounded-3xl border border-white/5">
            <h2 className="text-2xl font-bold text-white mb-4">MTD for High-Income Employees with Side Hustles</h2>
            <div className="prose prose-invert max-w-none text-slate-300">
              <p>
                A common misconception involves PAYE employment. If you earn £60,000 via a standard salary but only £5,000 through a weekend side hustle, you are <strong>not</strong> mandated for MTD. Your PAYE salary is completely excluded from the mandation test. Only your gross self-employed turnover and property income are combined to test against the £50,000 limit.
              </p>
            </div>
            <SectionSummary
              takeaway="PAYE income is excluded from the mandation test. Only self-employed turnover and property income counts toward the £50k limit."
              hmrcRef={<><a href="https://www.gov.uk/guidance/work-out-your-qualifying-income-for-making-tax-digital-for-income-tax" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-300 underline">Work Out Qualifying Income</a></>}
              actionText="Verify Joint Ownership rules"
              actionLink="/mtd-anxieties/mtd-for-landlords-joint-ownership"
            />
          </div>

          <div id="spoke-directory" className="w-full max-w-2xl mx-auto bg-indigo-900/20 border border-indigo-500/30 rounded-3xl p-8 shadow-2xl mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Directory of Specific MTD Anxieties</h2>
            <p className="text-slate-400 mb-6">Deep dives into common compliance edge-cases and exact HMRC rulings:</p>
            <ul className="space-y-4">
              <li><a href="/mtd-anxieties/mtd-for-landlords-joint-ownership" className="text-indigo-400 hover:text-indigo-300 font-bold decoration-indigo-400/30 underline underline-offset-4">Landlords: Joint Ownership Thresholds</a></li>
              <li><a href="/mtd-anxieties/mtd-digital-links-explained" className="text-indigo-400 hover:text-indigo-300 font-bold decoration-indigo-400/30 underline underline-offset-4">The "Copy-Paste" Rule: Digital Links under MTD</a></li>
              <li><a href="/mtd-anxieties/mtd-exemption-criteria" className="text-indigo-400 hover:text-indigo-300 font-bold decoration-indigo-400/30 underline underline-offset-4">Who qualifies for the "Digitally Excluded" exemption?</a></li>
              <li><a href="/mtd-anxieties/foreign-property-income" className="text-indigo-400 hover:text-indigo-300 font-bold decoration-indigo-400/30 underline underline-offset-4">Does Foreign Property Income count towards the £50k?</a></li>
              <li><a href="#mixed-income-separation" className="text-indigo-400 hover:text-indigo-300 font-bold decoration-indigo-400/30 underline underline-offset-4">How to separate Business and Personal expenses effectively</a></li>
            </ul>

            <div className="mt-8 pt-6 border-t border-indigo-500/20">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <span className="text-indigo-400 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-indigo-500/40 rounded">Tools</span>
                Compliance & Strategy Checkers
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <li><a href="/tools/mileage-calculator" className="text-emerald-400 hover:text-emerald-300 text-sm font-bold decoration-emerald-400/20 underline underline-offset-4">Mileage Claim Comparator</a></li>
                <li><a href="/tools/property-allowance" className="text-emerald-400 hover:text-emerald-300 text-sm font-bold decoration-emerald-400/20 underline underline-offset-4">Property Expense Decision Tree</a></li>
                <li><a href="/partners/onboarding-guide" className="text-emerald-400 hover:text-emerald-300 text-sm font-bold decoration-emerald-400/20 underline underline-offset-4">Partner Onboarding Guide</a></li>
                <li><a href="/partners/margin-calculator" className="text-emerald-400 hover:text-emerald-300 text-sm font-bold decoration-emerald-400/20 underline underline-offset-4">Partner Margin Calculator</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fact vs Fiction — Single authoritative section with FAQPage schema */}
      <section id="fact-hub" className="max-w-4xl mx-auto py-20 px-6 border-t border-white/5">
        <MTDFactHub />
      </section>

      {/* Footer CTA */}
      <section className="max-w-4xl mx-auto py-20 px-6 text-center">
        <div className="bg-gradient-to-br from-indigo-900/20 to-slate-900 border border-white/10 rounded-3xl p-12 shadow-2xl">
          <h2 className="text-3xl font-bold text-white mb-4">Ready for 2026?</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Use Zap Expenses to automate your digital records. No apps to download, no dashboards to learn — just snap and go via WhatsApp.
          </p>
          <a href="/#signup" className="inline-block px-10 py-4 bg-white text-slate-950 font-extrabold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-wide">
            Start Free Trial
          </a>
        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default MtdGuidePage;
